import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { numField } from "@/lib/health/body-profile";
import type { ActivityLevel } from "@/lib/health/fitness";
import {
  BodyProfileHint,
  useBodyProfileSync,
} from "@/lib/health/use-body-profile";
import { m } from "@/paraglide/messages";
import { getLocale } from "@/paraglide/runtime";
import { activityFactor, bmr, type Sex, tdee } from "../lib/health";
import { HealthDisclaimer } from "./HealthDisclaimer";
import { HeightInput, UnitsToggle, WeightInput } from "./inputs";
import { useBody } from "./useBody";

const ACTIVITIES = [
  "sedentary",
  "light",
  "moderate",
  "active",
  "veryActive",
] as const;

function activityLabel(a: (typeof ACTIVITIES)[number]): string {
  switch (a) {
    case "sedentary":
      return m.bmr_act_sedentary();
    case "light":
      return m.bmr_act_light();
    case "moderate":
      return m.bmr_act_moderate();
    case "active":
      return m.bmr_act_active();
    default:
      return m.bmr_act_very();
  }
}

const selectClass =
  "h-10 w-full rounded-md border border-border bg-card px-3 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

export function BmrCalc() {
  const b = useBody();
  const [age, setAge] = useState("30");
  const [sex, setSex] = useState<Sex>("male");
  const [activity, setActivity] = useState("moderate");
  const locale = getLocale();

  // Height/weight/units sync lives in useBody; this covers the rest.
  useBodyProfileSync({
    apply: (p) => {
      if (p.age) setAge(String(p.age));
      if (p.sex) setSex(p.sex);
      if (p.activity) setActivity(p.activity);
    },
    collect: () => ({
      age: numField(age),
      sex,
      activity: activity as ActivityLevel,
    }),
    deps: [age, sex, activity],
  });

  const bmrVal = bmr(b.weightKg, b.heightCm, Number(age), sex);
  const factor = activityFactor(activity);
  const tdeeVal =
    bmrVal !== null && factor !== undefined ? tdee(bmrVal, factor) : null;
  const fmt = (n: number) => Math.round(n).toLocaleString(locale);

  return (
    <div className="rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
      <div className="mb-5">
        <UnitsToggle value={b.units} onChange={b.setUnits} />
      </div>
      <div className="grid gap-4 sm:grid-cols-2">
        <HeightInput
          units={b.units}
          cm={b.cm}
          ft={b.ft}
          inch={b.inch}
          onCm={b.setCm}
          onFt={b.setFt}
          onInch={b.setInch}
          idPrefix="bmr"
        />
        <WeightInput
          units={b.units}
          kg={b.kg}
          lb={b.lb}
          onKg={b.setKg}
          onLb={b.setLb}
          idPrefix="bmr"
        />
        <div className="space-y-1.5">
          <Label htmlFor="bmr-age">{m.bmr_age()}</Label>
          <Input
            id="bmr-age"
            type="number"
            inputMode="numeric"
            value={age}
            onChange={(e) => setAge(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="bmr-sex">{m.bmr_sex()}</Label>
          <select
            id="bmr-sex"
            className={selectClass}
            value={sex}
            onChange={(e) => setSex(e.target.value as Sex)}
          >
            <option value="male">{m.bmr_sex_male()}</option>
            <option value="female">{m.bmr_sex_female()}</option>
          </select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label htmlFor="bmr-activity">{m.bmr_activity()}</Label>
          <select
            id="bmr-activity"
            className={selectClass}
            value={activity}
            onChange={(e) => setActivity(e.target.value)}
          >
            {ACTIVITIES.map((a) => (
              <option key={a} value={a}>
                {activityLabel(a)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div className="mt-6" aria-live="polite">
        {bmrVal === null ? (
          <p className="text-sm text-muted-foreground">{m.bmr_enter()}</p>
        ) : (
          <dl className="grid gap-3 sm:grid-cols-2">
            <div className="rounded-xl bg-primary/10 p-4 text-center">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {m.bmr_result_bmr()}
              </dt>
              <dd
                data-testid="bmr-value"
                className="mt-1 font-display text-2xl font-bold tabular-nums text-primary"
              >
                {m.bmr_kcal({ n: fmt(bmrVal) })}
              </dd>
            </div>
            <div className="rounded-xl bg-card/40 p-4 text-center">
              <dt className="text-xs uppercase tracking-wide text-muted-foreground">
                {m.bmr_result_tdee()}
              </dt>
              <dd className="mt-1 font-display text-2xl font-semibold tabular-nums">
                {tdeeVal !== null ? m.bmr_kcal({ n: fmt(tdeeVal) }) : "—"}
              </dd>
            </div>
          </dl>
        )}
      </div>

      <BodyProfileHint {...b.profileSync} />

      <HealthDisclaimer />
    </div>
  );
}
